import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 bg-orange-500 rounded-sm flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg">Corbel CRM</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm" className="rounded-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to App
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: May 21, 2026</p>

        <div className="prose prose-slate max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              Corbel CRM ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our customer relationship management platform designed for residential construction companies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, company name, and password when you create an account.</li>
              <li><strong>Business Data:</strong> Lead information, project details, client contacts, subcontractor information, and documents you upload to the platform.</li>
              <li><strong>Files & Photos:</strong> Site photos, documents, receipts, and other files you upload. Images in HEIC, DNG, or other formats are automatically converted to JPEG for compatibility. Both compressed and original versions may be stored.</li>
              <li><strong>Financial Data:</strong> Invoices, expenses, cost estimates, budget information, and payment records you enter into the platform. Estimate data imported from third-party tools (e.g., Craftsman National Estimator) is parsed and stored.</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through Stripe. We do not store your full credit card information.</li>
              <li><strong>Communications:</strong> Information you provide when contacting us for support.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">3. Information from Third-Party Services</h2>
            <p className="text-slate-600 leading-relaxed mb-4">When you connect third-party services, we may receive:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Google Account:</strong> Email address and calendar data when you connect Google Calendar or use Google Sign-In. We request access to send emails and manage calendar events on your behalf.</li>
              <li><strong>Stripe:</strong> Subscription status and payment confirmations.</li>
              <li><strong>Amazon Web Services (AWS):</strong> Files and photos you upload are stored securely in AWS S3 cloud storage. Access is controlled via time-limited signed URLs.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">4. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">5. Information Sharing</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We do not sell your personal information. We may share your information in the following situations:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., Stripe for payments, Google for calendar integration).</li>
              <li><strong>Legal Requirements:</strong> If required by law or in response to valid legal requests.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
              <li><strong>With Your Consent:</strong> When you direct us to share information with third parties.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">6. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of data in transit and at rest, secure authentication mechanisms, and regular security assessments.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide you services. You may request deletion of your account and associated data by contacting us. Some information may be retained as required by law or for legitimate business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Object to or restrict processing of your information</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">9. Cookies and Tracking</h2>
            <p className="text-slate-600 leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our platform and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-slate-600 mt-2">
              <strong>Email:</strong> support@corbel.work<br />
              <strong>Website:</strong> https://corbel.work
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t">
          <Link to="/terms" className="text-orange-500 hover:underline">
            View Terms of Service →
          </Link>
        </div>
      </main>
    </div>
  );
}
